import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency, validPrice } from '../../utils/formatting';
import { getAgingStatusColor } from '../../utils/unitDetailHelpers';
import { unitAgingBucketId, unitAgingLabel } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import {
    avatarTone,
    initials,
    prettifyPerson,
    priceCut,
    thumbLabel,
    TYPE_LABELS,
    unitDisplayName,
    unitYear,
} from '../../utils/taskBoard';
import useTaskCard, { MISSING_UNIT_TITLE } from '../../hooks/useTaskCard';
import CancelTaskConfirm from '../shared/CancelTaskConfirm';
import DeleteTaskConfirm from '../shared/DeleteTaskConfirm';

// Kept for existing imports; the labels live in utils/taskBoard.js.
export { TYPE_LABELS };

/**
 * One task on the board. Actions sit in a corner overlay that appears on hover
 * or keyboard focus; "Cancel task", Edit and Delete live behind "More actions"
 * so a stray click can never close a task. The behaviour is
 * hooks/useTaskCard, shared with the redesigned card (TasksV2).
 *
 * unitStatus: 'found' (unit joined), 'missing' (inventory loaded, unit not in
 * it — nothing to open), or 'unknown' (inventory not loaded / not available).
 */
function TaskCard({
    task, unit, unitStatus = unit ? 'found' : 'unknown', assigneeName, canEdit, draggable = canEdit,
    onStatusChange, onQuickStatus, onNavigate, hintId,
    onOpenUnit, onDelete, onEdit, dragging, onDragStart, onDragEnd,
}) {
    const card = useTaskCard({
        task, unitStatus, canEdit, onStatusChange, onQuickStatus, onNavigate, onOpenUnit, onDelete, onEdit,
    });
    const {
        menuOpen, confirming, editing, editTitle, editDetails, editDueDate,
        setEditTitle, setEditDetails, setEditDueDate, moreBtnRef, menuRef, titleInputRef, menuId,
        typeLabel, type: typeKey, open, due, canOpen, active, quick,
    } = card;
    const stop = (e) => e.stopPropagation();
    const { aging } = useDealershipSettings();
    const ageLabel = unit ? unitAgingLabel(unit, aging) : '';

    const classes = [
        'task-card',
        due?.late ? 'task-card--overdue' : '',
        open ? '' : 'task-card--closed',
        active ? 'task-card--active' : '',
        editing ? 'task-card--editing' : '',
        dragging ? 'task-card--dragging' : '',
        canOpen ? '' : 'task-card--no-unit',
    ].filter(Boolean).join(' ');

    const assignee = task.assigned_to;
    const shownName = assignee ? (assigneeName || prettifyPerson(assignee)) : '';
    const assigneeLabel = assignee ? `Assigned to ${shownName} (${assignee})` : 'Unassigned';
    const year = unit ? unitYear(unit) : null;
    const unitName = unit ? unitDisplayName(unit) : '';
    const unitTitle = `${unitName}${year ? ` · ${year}` : ''}`;
    const cut = task.type === 'price_change' ? priceCut(task) : null;
    const aiValue = unit ? validPrice(unit.PredictedPrice) : null;
    const daysInStock = unit && unit.DaysInStock != null ? Number(unit.DaysInStock) : null;

    return (
        <article
            className={classes}
            tabIndex={0}
            aria-label={`${typeLabel}: ${task.title}`}
            title={canOpen ? undefined : MISSING_UNIT_TITLE}
            data-task-id={task.id}
            aria-describedby={hintId}
            aria-keyshortcuts={canEdit ? 'ArrowUp ArrowDown ArrowLeft ArrowRight Enter S C' : 'ArrowUp ArrowDown ArrowLeft ArrowRight Enter'}
            draggable={Boolean(draggable) && !editing}
            onDragStart={draggable ? (e) => { if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; onDragStart?.(task); } : undefined}
            onDragEnd={draggable ? () => onDragEnd?.() : undefined}
            onClick={card.handleCardClick}
            onKeyDown={card.handleCardKeyDown}
        >
            <div className="task-card-top">
                <span className={`task-type task-type--${typeKey}`}>{typeLabel}</span>
                <span className="task-stock">#{task.stock_number}</span>
            </div>

            {canEdit && !editing && (
                // mousedown keeps focus where it is, so clicking inside the
                // overlay never blurs (and closes) the open menu first.
                <div className="task-card-quick" onClick={stop} onMouseDown={(e) => e.preventDefault()} onBlur={card.handleQuickBlur}>
                    {open ? (
                        <>
                            {task.status === 'open' && (
                                <button type="button" className="task-quick-btn" onClick={quick('in_progress')}>Start</button>
                            )}
                            <button type="button" className="task-quick-btn task-quick-btn--go" onClick={quick('completed')}>Complete</button>
                        </>
                    ) : (
                        <button type="button" className="task-quick-btn" aria-label="Reopen task" onClick={quick('open')}>Reopen</button>
                    )}
                    <button
                        type="button"
                        ref={moreBtnRef}
                        className="task-quick-btn task-quick-btn--more"
                        aria-label="More actions"
                        aria-haspopup="true"
                        aria-expanded={menuOpen}
                        aria-controls={menuOpen ? menuId : undefined}
                        onClick={card.toggleMenu}
                    >
                        <span aria-hidden="true">⋯</span>
                    </button>
                    {menuOpen && (
                        <div className="task-card-menu" id={menuId} ref={menuRef} onKeyDown={card.handleMenuKeyDown}>
                            <button type="button" className="task-menu-item" onClick={card.chooseEdit}>Edit</button>
                            {open && (
                                <button
                                    type="button"
                                    className="task-menu-item task-menu-item--danger"
                                    onClick={card.chooseCancel}
                                >
                                    Cancel task
                                </button>
                            )}
                            <button
                                type="button"
                                className="task-menu-item task-menu-item--danger"
                                onClick={card.chooseDelete}
                            >
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            )}

            {unit && (
                <div className="task-unit">
                    {/* The inventory payload carries no photo URL (photos load
                        per unit), so the category code stands in. */}
                    <span className="task-thumb" aria-hidden="true">{thumbLabel(unit)}</span>
                    <span className="task-unit-meta">
                        <span className="task-unit-line" title={unitTitle}>
                            <span className="task-unit-name">{unitName}</span>
                            {year && <span className="task-unit-year">{`\u00a0· ${year}`}</span>}
                        </span>
                        {unit.BranchName && <span className="task-unit-branch">{unit.BranchName}</span>}
                    </span>
                </div>
            )}
            {unitStatus === 'missing' && (
                <div className="task-unit-missing">Not in current inventory</div>
            )}

            {editing ? (
                <div className="task-edit-form" onClick={stop} onKeyDown={stop}>
                    <label className="task-edit-field">
                        <span>Title</span>
                        <input ref={titleInputRef} type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={800} />
                    </label>
                    <label className="task-edit-field">
                        <span>Due date</span>
                        <input type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} />
                    </label>
                    <label className="task-edit-field">
                        <span>Details</span>
                        <textarea value={editDetails} onChange={(e) => setEditDetails(e.target.value)} rows={3} />
                    </label>
                    <div className="task-edit-actions">
                        <button type="button" className="task-btn" onClick={card.handleDiscardEdit}>Discard changes</button>
                        <button type="button" className="task-btn task-btn-complete" onClick={card.handleSaveEdit} disabled={!editTitle.trim()}>Save changes</button>
                    </div>
                </div>
            ) : (
                <p className="task-card-title">{task.title}</p>
            )}

            {task.type === 'price_change' && task.suggested_price != null && (
                <div className="task-price-row">
                    {cut && <s className="task-price-old">{formatCurrency(cut.from)}</s>}
                    <span className="task-price-arrow" aria-hidden="true">→</span>
                    <span className="task-price-new">{formatCurrency(task.suggested_price)}</span>
                    {cut && cut.change !== 0 && (
                        <span className={`task-price-delta${cut.change > 0 ? ' task-price-delta--up' : ''}`}>{cut.label}</span>
                    )}
                    {aiValue != null && <span className="task-price-ai">AI value {formatCurrency(aiValue)}</span>}
                </div>
            )}

            <div className="task-card-meta">
                {assignee ? (
                    <span
                        className={`task-avatar task-avatar--tone-${avatarTone(assignee)}`}
                        role="img"
                        title={assigneeLabel}
                        aria-label={assigneeLabel}
                    >
                        {initials(shownName)}
                    </span>
                ) : (
                    <span className="task-avatar task-avatar--none" role="img" title="Unassigned" aria-label="Unassigned">+</span>
                )}
                {due && <span className={`task-pill task-pill--${due.tone}`}>{due.text}</span>}
                {daysInStock != null && Number.isFinite(daysInStock) && (
                    <span className="task-age" title={ageLabel ? `Days in stock: ${ageLabel}` : 'Days in stock'}>
                        <i className="task-age-dot" aria-hidden="true" style={{ backgroundColor: getAgingStatusColor(unitAgingBucketId(unit, aging)) }} />
                        {`${daysInStock.toLocaleString('en-US')}d in stock`}
                    </span>
                )}
            </div>

            {confirming === 'cancel' && (
                <div className="task-card-confirm" onClick={stop} onKeyDown={stop}>
                    {/* Cancelling is a status change, so it still asks first and
                        ignores a double-click (shared with the Activity tab). */}
                    <CancelTaskConfirm
                        classPrefix="task"
                        buttonClass="task-btn"
                        dangerClass="task-btn-cancel"
                        onKeep={card.keepTask}
                        onConfirm={card.confirmCancel}
                    />
                </div>
            )}
            {confirming === 'delete' && (
                <div className="task-card-confirm" onClick={stop} onKeyDown={stop}>
                    <DeleteTaskConfirm
                        classPrefix="task"
                        buttonClass="task-btn"
                        dangerClass="task-btn-cancel"
                        onKeep={card.keepTask}
                        onConfirm={card.confirmDelete}
                    />
                </div>
            )}
        </article>
    );
}

TaskCard.propTypes = {
    task: PropTypes.object.isRequired,
    unit: PropTypes.object,
    unitStatus: PropTypes.oneOf(['found', 'missing', 'unknown']),
    assigneeName: PropTypes.string,
    canEdit: PropTypes.bool.isRequired,
    draggable: PropTypes.bool,
    onStatusChange: PropTypes.func.isRequired,
    onQuickStatus: PropTypes.func,
    onNavigate: PropTypes.func,
    hintId: PropTypes.string,
    onOpenUnit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    dragging: PropTypes.bool,
    onDragStart: PropTypes.func,
    onDragEnd: PropTypes.func,
};

export default TaskCard;
